import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, CheckCircle, FileText, Send } from 'lucide-react';
import ReactGA from 'react-ga4';
import SocietyFields from './SocietyFields';
import LandOwnerFields from './LandOwnerFields';
import BuilderFields from './BuilderFields';
import AgentFields from './AgentFields';
import InvestorFields from './InvestorFields';
import { supabase } from '../supabaseClient';
import { NOTIFICATION_EMAIL, DASHBOARD_URL } from '../constants';

const ContactForm = ({ role, setRole }) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 5000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: null });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) setFormData({ ...formData, document: file });
    };

    const validate = () => {
        let newErrors = {};
        if (!formData.description) newErrors.description = 'Description is required';

        const requirements = {
            Society: { societyName: 'Society Name', contact: 'Contact number', pincode: 'Pincode', division: 'Division' },
            LandOwner: { landOwnerName: 'Land Owner Name', contact: 'Contact number', pincode: 'Pincode', division: 'Division' },
            Builder: { builderName: 'Builder Name', contact: 'Contact number', email: 'Email' },
            Agent: { name: 'Full Name', contact: 'Contact number' },
            Investor: { investorType: 'Investor type', name: formData.investorType === 'Institutional' ? 'Institution Name' : 'Full Name', contact: 'Contact number', email: 'Email' }
        };

        const fields = requirements[role];
        Object.keys(fields).forEach(key => {
            if (!formData[key]) {
                newErrors[key] = `${fields[key]} is required`;
            } else if (key === 'email') {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData[key])) {
                    newErrors[key] = 'Invalid email format';
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            showNotification('error', Object.values(errors)[0] || "Please fill in all required fields.");
            return;
        }

        setIsSubmitting(true);
        try {
            if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
                throw new Error("Missing Supabase Credentials. Please restart your 'npm start'.");
            }

            let documentUrl = null;
            if (formData.document) {
                const file = formData.document;
                const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${file.name.split('.').pop()}`;
                const { error: uploadError } = await supabase.storage.from('inquiry-documents').upload(`${role}/${fileName}`, file);
                if (uploadError) throw new Error(`Upload Failed: ${uploadError.message}`);
                const { data: { publicUrl } } = supabase.storage.from('inquiry-documents').getPublicUrl(`${role}/${fileName}`);
                documentUrl = publicUrl;
            }

            const { document, ...cleanFormData } = formData;
            const { error: dbError } = await supabase.from('inquiries').insert([{ role, form_data: { ...cleanFormData, document_link: documentUrl } }]);
            if (dbError) throw new Error(`Database Error: ${dbError.message}`);

            const entityName = formData.societyName || formData.landOwnerName || formData.builderName || formData.name || 'a new lead';
            const emailData = new FormData();
            emailData.append("_subject", `New Inquiry: ${entityName} (${role})`);
            emailData.append("_template", "table");
            emailData.append("_captcha", "false");
            emailData.append("Role", role);
            emailData.append("Entity_Name", entityName);

            Object.keys(cleanFormData).forEach(key => {
                const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                emailData.append(label, cleanFormData[key]);
            });
            if (documentUrl) emailData.append("Document_Link", documentUrl);
            emailData.append("Dashboard_Action", `Click here to view in Dashboard: ${DASHBOARD_URL}`);

            await fetch(`https://formsubmit.co/ajax/${NOTIFICATION_EMAIL}`, { method: "POST", body: emailData });

            // Track Google Analytics conversion event
            try {
                ReactGA.event({
                    category: 'Lead',
                    action: 'Form Submission',
                    label: role
                });
            } catch (gaError) {
                console.error('GA event error:', gaError);
            }

            showNotification('success', 'Inquiry submitted successfully! We will get back to you soon.');
            setFormData({});
        } catch (error) {
            showNotification('error', `Submission failed: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="contact" className="pt-12 sm:pt-24 pb-16 sm:pb-24 bg-white relative overflow-hidden">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                <div className="mb-10 sm:mb-12">
                    <div className="inline-block px-5 py-2 mb-6 sm:mb-8 rounded-full bg-black shadow-sm">
                        <span className="text-white text-xs sm:text-sm font-black uppercase tracking-widest">Get In Touch</span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-sans font-bold text-primary tracking-tight mb-3 sm:mb-4">Find your dream redevelopment solution</h2>
                    <p className="text-tertiary font-sans text-base sm:text-lg font-medium px-4 sm:px-0">Connecting you with the perfect partners for your journey</p>
                </div>

                <div className="bg-white p-6 sm:p-12 rounded-[2rem] sm:rounded-[3.5rem] w-full shadow-2xl border border-gray-100 px-4 sm:px-12 mx-auto">
                    <form onSubmit={handleSubmit}>
                        <div className="flex flex-col bg-bgLight/50 rounded-[2.5rem] p-3 border border-gray-100 divide-y divide-gray-200/50">
                            <div className="w-full p-5 flex flex-col sm:flex-row items-center justify-start gap-4">
                                <label className="text-[10px] font-extrabold text-secondary uppercase tracking-widest px-2">I am a</label>
                                <div className="grid grid-cols-2 gap-1.5 sm:flex sm:gap-2 w-full sm:w-auto">
                                    {[
                                        { id: 'Society', label: 'Society' },
                                        { id: 'LandOwner', label: 'Land Owner' },
                                        { id: 'Builder', label: 'Builder' },
                                        { id: 'Agent', label: 'Agent' },
                                        { id: 'Investor', label: 'Investor' }
                                    ].map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => setRole(tab.id)}
                                            className={`w-full sm:w-auto px-2 sm:px-6 py-2.5 rounded-full text-[12px] sm:text-sm font-bold whitespace-nowrap transition-all 
                                                ${tab.id === 'Investor' ? 'col-span-2' : ''}
                                                ${role === tab.id ? 'bg-primary text-white shadow-lg' : 'bg-white text-tertiary hover:bg-gray-50 border border-gray-100'}`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {role === 'Society' && <SocietyFields handleInputChange={handleInputChange} errors={errors} />}
                            {role === 'LandOwner' && <LandOwnerFields handleInputChange={handleInputChange} errors={errors} />}
                            {role === 'Builder' && <BuilderFields handleInputChange={handleInputChange} errors={errors} />}
                            {role === 'Agent' && <AgentFields handleInputChange={handleInputChange} errors={errors} />}
                            {role === 'Investor' && <InvestorFields handleInputChange={handleInputChange} errors={errors} formData={formData} />}

                            <div className={`w-full p-5 flex flex-col justify-center text-left hover:bg-white/50 transition-colors ${errors.description ? 'bg-red-50/50' : ''}`}>
                                <label className={`text-[10px] font-extrabold uppercase tracking-widest mb-2 px-1 ${errors.description ? 'text-red-500' : 'text-secondary'}`}>
                                    Description <span className="text-red-500">*</span> {errors.description && <span className="lowercase font-medium ml-2">({errors.description})</span>}
                                </label>
                                <div className="flex items-start px-1">
                                    <FileText className={`w-4 h-4 mr-3 mt-1 ${errors.description ? 'text-red-500' : 'text-primary'}`} />
                                    <textarea
                                        name="description"
                                        placeholder="Tell us your story..."
                                        rows="3"
                                        className="w-full outline-none font-bold text-primary placeholder-tertiary/50 bg-transparent text-base resize-none"
                                        onChange={handleInputChange}
                                        value={formData.description || ''}
                                    ></textarea>
                                </div>
                            </div>

                            <div className="w-full p-5 hover:bg-white/50 transition-colors">
                                <label className="text-[10px] font-extrabold text-secondary uppercase tracking-widest mb-3 px-1 block text-left">Upload Document</label>
                                <label className="flex items-center justify-center w-full h-32 border-2 border-gray-200 border-dashed rounded-3xl cursor-pointer bg-white hover:bg-gray-50 transition-all hover:border-secondary/50">
                                    <div className="flex flex-col items-center justify-center">
                                        <Upload className="w-6 h-6 mb-2 text-secondary" />
                                        <p className="text-sm text-tertiary font-bold px-4">{formData.document ? `Success: ${formData.document.name}` : 'Click to upload files (optional)'}</p>
                                    </div>
                                    <input type="file" className="hidden" onChange={handleFileChange} />
                                </label>
                            </div>

                            <div className="w-full p-3 pt-6">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-logo-gradient hover-logo-gradient text-white px-8 h-14 sm:h-16 rounded-2xl sm:rounded-3xl font-bold flex items-center justify-center gap-3 transition-all shadow-xl hover:shadow-primary/20 active:scale-[0.98] whitespace-nowrap text-base sm:text-lg"
                                >
                                    {isSubmitting ? 'Processing...' : <><Send className="w-5 h-5" /><span>Send Inquiry</span></>}
                                </button>
                            </div>
                        </div>
                    </form>

                    <div className="fixed top-24 right-4 sm:right-8 z-[100] pointer-events-none">
                        {notification && (
                            <motion.div
                                initial={{ opacity: 0, x: 50, scale: 0.9 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: 20, scale: 0.9 }}
                                className={`p-4 rounded-2xl shadow-2xl flex items-center gap-4 border pointer-events-auto min-w-[300px] text-left ${notification.type === 'success' ? 'bg-white border-green-100 text-green-800' : 'bg-white border-red-100 text-red-800'}`}
                            >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${notification.type === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
                                    {notification.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />}
                                </div>
                                <div className="flex-1">
                                    <p className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-0.5">{notification.type === 'success' ? 'Success' : 'Attention'}</p>
                                    <p className="text-sm font-bold font-sans leading-tight">{notification.message}</p>
                                </div>
                                <button onClick={() => setNotification(null)} className="opacity-30 hover:opacity-100 text-lg p-1">&times;</button>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactForm;


